import { Fragment, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Copy, Mail, Pause, Pencil, Play, Send, Trash2 } from "lucide-react";
import { api, ApiError, FORMAT_LABELS, type Schedule } from "../../lib/api";
import { presetLabel } from "../../lib/time";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Dialog } from "../../components/ui/overlay";
import { Skeleton, Tip } from "../../components/ui/primitives";
import { describeCadence, formatWhen } from "./cadence";
import { EditScheduleDialog } from "./ScheduleDialog";
import { LastRun, RunHistory, ScheduleStatus } from "./ScheduleParts";

/** My schedules: every scheduled report email you own, with its runs, and pause / resume / send now. */
export function SchedulesPage() {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, staleTime: Infinity });
  const schedules = useQuery({ queryKey: ["schedules"], queryFn: api.schedules, retry: false });
  const [editing, setEditing] = useState<{ id: string; duplicate?: boolean }>();
  const [deleting, setDeleting] = useState<Schedule>();
  const staffSession = me.data?.userId.startsWith("staff:") ?? false;

  return (
    <div className="mx-auto max-w-[1400px] px-6 pb-16">
      <header className="sticky top-0 z-20 -mx-6 mb-4 flex flex-wrap items-center gap-3 border-b border-border bg-background/85 px-6 py-3 backdrop-blur">
        <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight"><Mail className="size-4 text-subtle" /> My schedules</h1>
        <span className="text-sm text-muted">Dashboards you email on a schedule. To add one, open a dashboard and choose Schedule email from its menu.</span>
      </header>
      <ScheduleTable
        query={schedules}
        empty="You haven't scheduled any dashboards yet."
        forbidden="Only GoTab staff can schedule reports for now."
        actions={(s) => (
          <Actions s={s} onEdit={() => setEditing({ id: s.id })} onDuplicate={() => setEditing({ id: s.id, duplicate: true })} onDelete={() => setDeleting(s)} />
        )}
      />
      {editing && (
        <EditScheduleDialog
          key={`${editing.id}:${editing.duplicate ?? false}`} id={editing.id} duplicate={editing.duplicate}
          open onOpenChange={(o) => !o && setEditing(undefined)} staffSession={staffSession}
        />
      )}
      {deleting && <DeleteScheduleDialog s={deleting} onClose={() => setDeleting(undefined)} />}
    </div>
  );
}

/**
 * Schedules with their status, next send and latest run; a row opens to its run history. `actions`
 * for the owner's own; without it (super users' view of everyone's) it's read-only and names owners.
 */
export function ScheduleTable({ query, empty, forbidden, actions }: {
  query: { isLoading: boolean; error: Error | null; data?: Schedule[] };
  empty: string;
  forbidden: string;
  actions?: (s: Schedule) => ReactNode;
}) {
  const [open, setOpen] = useState<string>();
  if (query.isLoading) return <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12" />)}</div>;
  if (query.error) return <p className="text-sm text-bad">{query.error instanceof ApiError && query.error.status === 403 ? forbidden : query.error.message}</p>;
  const rows = query.data ?? [];
  if (!rows.length) return <p className="mt-16 text-center text-sm text-muted">{empty}</p>;

  const headers = ["", "Dashboard", ...(actions ? [] : ["Owner"]), "When", "Recipients", "Status", "Next send", "Last run", ...(actions ? [""] : [])];
  const cell = "border-b border-border px-3 py-2.5 align-top";
  return (
    <div className="overflow-x-auto rounded-[14px] border border-border bg-surface">
      <table className="w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>{headers.map((h, i) => <th key={i} className="border-b border-border px-3 py-2 text-left text-xs font-medium whitespace-nowrap text-muted">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((s) => {
            const expanded = open === s.id;
            return (
              <Fragment key={s.id}>
                <tr className={cn("hover:bg-surface-2/60", expanded && "bg-surface-2/40")}>
                  <td className={cn(cell, "w-8 pr-0")}>
                    <button
                      onClick={() => setOpen(expanded ? undefined : s.id)}
                      aria-expanded={expanded}
                      aria-label={expanded ? "Hide runs" : "Show runs"}
                      className="rounded p-0.5 text-subtle hover:bg-surface-2 hover:text-foreground"
                    >
                      {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                    </button>
                  </td>
                  <td className={cell}>
                    {s.dashboardTitle === null ? (
                      <span className="text-subtle">Deleted dashboard</span>
                    ) : actions ? (
                      <Link to="/d/$id" params={{ id: s.dashboardId }} className="font-medium hover:underline">{s.dashboardTitle || "Untitled"}</Link>
                    ) : (
                      <span className="font-medium">{s.dashboardTitle || "Untitled"}</span>
                    )}
                    <span className="block text-xs text-subtle">
                      {s.widgetIds ? `${s.widgetIds.length} widget${s.widgetIds.length === 1 ? "" : "s"}` : "Whole dashboard"} · {FORMAT_LABELS[s.format]} · {s.locationIds.length} location{s.locationIds.length === 1 ? "" : "s"}
                    </span>
                  </td>
                  {!actions && (
                    <td className={cell}>
                      {s.owner.name ?? s.ownerEmail ?? s.ownerId}
                      {s.owner.name && s.ownerEmail && <span className="block text-xs text-subtle">{s.ownerEmail}</span>}
                    </td>
                  )}
                  <td className={cell}>
                    {describeCadence(s.cadence)}
                    <span className="block text-xs text-subtle">{presetLabel(s.timePreset)} · {s.timezone.replace(/_/g, " ")}</span>
                  </td>
                  <td className={cn(cell, "max-w-64")}>
                    <Tip content={s.people.map((p) => p.email ?? p.name).join(", ")}>
                      <span className="line-clamp-2">{s.people.map((p) => p.name).join(", ") || <span className="text-subtle">No one</span>}</span>
                    </Tip>
                  </td>
                  <td className={cell}><ScheduleStatus s={s} /></td>
                  <td className={cn(cell, "whitespace-nowrap tabular text-muted")} title={s.nextRunAt}>{s.status === "active" ? formatWhen(s.nextRunAt) : "—"}</td>
                  <td className={cell}><LastRun run={s.lastRun} /></td>
                  {actions && <td className={cn(cell, "whitespace-nowrap text-right")}>{actions(s)}</td>}
                </tr>
                {expanded && (
                  <tr>
                    <td />
                    <td colSpan={headers.length - 1} className="border-b border-border px-3 pb-4 pt-1"><RunHistory scheduleId={s.id} /></td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Actions({ s, onEdit, onDuplicate, onDelete }: { s: Schedule; onEdit: () => void; onDuplicate: () => void; onDelete: () => void }) {
  const qc = useQueryClient();
  const [notice, setNotice] = useState<string>();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["schedules"] });
    void qc.invalidateQueries({ queryKey: ["schedule", s.id] });
  };
  const toggle = useMutation({
    mutationFn: () => (s.status === "active" ? api.pauseSchedule(s.id) : api.resumeSchedule(s.id)),
    onSuccess: refresh,
  });
  const send = useMutation({
    mutationFn: () => api.sendScheduleNow(s.id),
    onSuccess: () => {
      setNotice("Queued. It should arrive in a minute or two.");
      // The run shows up once the queue has sent it.
      setTimeout(refresh, 15_000);
    },
  });
  const error = toggle.error ?? send.error;
  return (
    <span className="inline-flex flex-col items-end gap-1">
      <span className="inline-flex items-center gap-0.5">
        <Tip content="Send now">
          <Button size="icon-sm" variant="ghost" aria-label="Send now" disabled={send.isPending} onClick={() => send.mutate()}><Send className="size-3.5" /></Button>
        </Tip>
        <Tip content={s.status === "active" ? "Pause" : "Resume"}>
          <Button size="icon-sm" variant="ghost" aria-label={s.status === "active" ? "Pause" : "Resume"} disabled={toggle.isPending} onClick={() => toggle.mutate()}>
            {s.status === "active" ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
          </Button>
        </Tip>
        <Tip content="Edit">
          <Button size="icon-sm" variant="ghost" aria-label="Edit" onClick={onEdit}><Pencil className="size-3.5" /></Button>
        </Tip>
        <Tip content="Duplicate">
          <Button size="icon-sm" variant="ghost" aria-label="Duplicate" onClick={onDuplicate}><Copy className="size-3.5" /></Button>
        </Tip>
        <Tip content="Delete">
          <Button size="icon-sm" variant="ghost" aria-label="Delete" onClick={onDelete}><Trash2 className="size-3.5" /></Button>
        </Tip>
      </span>
      {error ? <span className="max-w-56 whitespace-normal text-xs text-bad">{error.message}</span>
        : notice && <span className="max-w-56 whitespace-normal text-xs text-subtle">{notice}</span>}
    </span>
  );
}

function DeleteScheduleDialog({ s, onClose }: { s: Schedule; onClose: () => void }) {
  const qc = useQueryClient();
  const del = useMutation({
    mutationFn: () => api.deleteSchedule(s.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["schedules"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Delete schedule" className="w-[440px] max-w-[calc(100vw-32px)] p-6">
      <h2 className="text-base font-semibold tracking-tight">Delete this schedule?</h2>
      <p className="mt-1 text-sm text-muted">
        “{s.dashboardTitle || "Untitled"}”, {describeCadence(s.cadence).toLowerCase()}, stops sending. Its past runs are kept.
      </p>
      {del.error && <p className="mt-3 text-sm text-bad">{del.error.message}</p>}
      <div className="mt-6 flex justify-end gap-2">
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="danger" disabled={del.isPending} onClick={() => del.mutate()}>Delete</Button>
      </div>
    </Dialog>
  );
}
